import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function AboutEditor() {
  const { data } = useQuery({ queryKey: ["about-admin"], queryFn: () => api.get("/about").then((r) => r.data) });
  const [form, setForm] = useState({ story: "", testimonials: [], contact_rows: [] });

  useEffect(() => {
    if (data) setForm({ story: data.story || "", testimonials: data.testimonials || [], contact_rows: data.contact_rows || [] });
  }, [data]);

  const save = async () => {
    try {
      await api.put("/about", form);
      toast.success("About page saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  const setT = (i, k, v) => setForm((f) => ({ ...f, testimonials: f.testimonials.map((t, idx) => idx === i ? { ...t, [k]: v } : t) }));
  const setR = (i, k, v) => setForm((f) => ({ ...f, contact_rows: f.contact_rows.map((t, idx) => idx === i ? { ...t, [k]: v } : t) }));

  return (
    <div data-testid="admin-about-editor" className="max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-white">About Editor</h1>
        <button onClick={save} data-testid="save-about-btn" className="btn-gold rounded-full px-6 py-2.5 text-sm font-mono-label uppercase flex items-center gap-2"><Save size={15} /> Save</button>
      </div>

      <label className="text-white/60 text-xs uppercase tracking-wide font-mono-label mb-1.5 block">Studio Story</label>
      <Textarea value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })} rows={6} className="bg-[#0a0a0a] border-white/15 mb-8" data-testid="about-story" />

      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-xl text-white">Testimonials</h2>
        <button onClick={() => setForm({ ...form, testimonials: [...form.testimonials, { name: "", role: "", quote: "" }] })} data-testid="add-testimonial-btn" className="text-sm text-[#D4AF37] flex items-center gap-1"><Plus size={14} /> Add</button>
      </div>
      <div className="space-y-3 mb-8">
        {form.testimonials.map((t, i) => (
          <div key={i} className="bg-[#111] border border-white/10 rounded-xl p-4 space-y-2" data-testid={`testimonial-edit-${i}`}>
            <div className="flex gap-2">
              <Input placeholder="Name" value={t.name} onChange={(e) => setT(i, "name", e.target.value)} className="bg-[#0a0a0a] border-white/15" />
              <Input placeholder="Role" value={t.role} onChange={(e) => setT(i, "role", e.target.value)} className="bg-[#0a0a0a] border-white/15" />
              <button onClick={() => setForm({ ...form, testimonials: form.testimonials.filter((_, idx) => idx !== i) })} className="text-white/40 hover:text-red-400 shrink-0"><Trash2 size={16} /></button>
            </div>
            <Textarea placeholder="Quote" value={t.quote} onChange={(e) => setT(i, "quote", e.target.value)} className="bg-[#0a0a0a] border-white/15" rows={2} />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-xl text-white">Contact Rows</h2>
        <button onClick={() => setForm({ ...form, contact_rows: [...form.contact_rows, { label: "", value: "" }] })} data-testid="add-contact-btn" className="text-sm text-[#D4AF37] flex items-center gap-1"><Plus size={14} /> Add</button>
      </div>
      <div className="space-y-2">
        {form.contact_rows.map((r, i) => (
          <div key={i} className="flex gap-2" data-testid={`contact-edit-${i}`}>
            <Input placeholder="Label (Phone/Email/Address)" value={r.label} onChange={(e) => setR(i, "label", e.target.value)} className="bg-[#0a0a0a] border-white/15 w-48" />
            <Input placeholder="Value" value={r.value} onChange={(e) => setR(i, "value", e.target.value)} className="bg-[#0a0a0a] border-white/15" />
            <button onClick={() => setForm({ ...form, contact_rows: form.contact_rows.filter((_, idx) => idx !== i) })} className="text-white/40 hover:text-red-400 shrink-0"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
